import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { App, DatePicker, Form, Input, InputNumber, Select } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import type { z } from 'zod'
import type { billDetailSchema } from '@super-mega/server/contracts/bill'
import { AttachmentUpload } from '@/features/files/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { OrganizationAccountSelect } from './organization-account-select.tsx'

type Values = {
  amount: string
  occurredAt: Dayjs
  organizationAccountId: number
  channel: string
  counterpartyName: string
  counterpartyAccount?: string
  attachmentIds: number[]
  note?: string
  allocationNote?: string
}
export function BillMoneyForm({
  bill,
  onClose,
  onSaved
}: {
  bill: z.infer<typeof billDetailSchema>
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const access = useAccess()
  const permissionKind = bill.direction === 'receivable' ? 'receipt' : 'payment'
  const confirm = access.can(`bill.${permissionKind}.confirm`)
  const trpc = useTRPC(),
    { message } = App.useApp()
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const save = useMutation(trpc.bill.recordMoney.mutationOptions())
  return (
    <ModalForm<Values>
      open
      title={bill.direction === 'receivable' ? '登记实际收款' : '登记实际付款'}
      layout="vertical"
      modalProps={{ onCancel: onClose, destroyOnHidden: true, mask: { closable: false } }}
      initialValues={{
        amount: bill.outstandingAmount,
        occurredAt: dayjs(),
        channel: 'bankTransfer',
        counterpartyName: bill.customerName,
        attachmentIds: []
      }}
      submitter={{ submitButtonProps: { loading: save.isPending } }}
      onFinish={async (v) => {
        try {
          await save.mutateAsync({
            organizationId: bill.organizationId,
            organizationAccountId: v.organizationAccountId,
            customerId: bill.customerId,
            payeeId: bill.payeeId,
            kind: bill.direction === 'receivable' ? 'receipt' : 'payment',
            amount: v.amount,
            occurredAt: v.occurredAt.toDate(),
            channel: v.channel,
            counterpartySnapshot: { name: v.counterpartyName, accountNo: v.counterpartyAccount },
            attachmentIds: v.attachmentIds,
            note: v.note ?? null,
            status: confirm ? 'confirmed' : 'pending',
            idempotencyKey,
            allocations: [{ billId: bill.id, amount: v.amount, note: v.allocationNote ?? null }]
          })
          await onSaved()
          void message.success(confirm ? '资金流水已确认' : '已保存待确认流水')
          return true
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '登记失败')
          return false
        }
      }}
    >
      <p>本次金额全部分配到 {bill.billNo}；可以分次登记。</p>
      <Form.Item name="amount" label="实际金额" rules={[{ required: true }]}>
        <InputNumber stringMode min="0.01" max={bill.outstandingAmount} precision={2} className="w-full" />
      </Form.Item>
      <Form.Item name="occurredAt" label="实际收付时间" rules={[{ required: true }]}>
        <DatePicker showTime className="w-full" />
      </Form.Item>
      <Form.Item
        name="organizationAccountId"
        label={bill.direction === 'receivable' ? '主体收款账户' : '主体付款账户'}
        rules={[{ required: true }]}
      >
        <OrganizationAccountSelect
          purpose={bill.direction === 'receivable' ? 'bill.receipt.record' : 'bill.payment.record'}
          organizationId={bill.organizationId}
        />
      </Form.Item>
      <Form.Item name="channel" label="收付方式" rules={[{ required: true }]}>
        <Select
          options={[
            { value: 'bankTransfer', label: '银行转账' },
            { value: 'wechat', label: '微信' },
            { value: 'alipay', label: '支付宝' },
            { value: 'cash', label: '现金' }
          ]}
        />
      </Form.Item>
      <Form.Item name="counterpartyName" label="实际对方户名" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="counterpartyAccount" label="实际对方账号">
        <Input />
      </Form.Item>
      <Form.Item name="attachmentIds" label="截图或回单">
        <AttachmentUpload purpose="moneyAttachment" maxCount={30} />
      </Form.Item>
      <Form.Item name="note" label="流水共同备注">
        <Input.TextArea maxLength={5000} />
      </Form.Item>
      <Form.Item name="allocationNote" label="备注">
        <Input.TextArea maxLength={5000} />
      </Form.Item>
    </ModalForm>
  )
}
